import { setCalendarSidebarFilters } from '@common/reducers/CalendarReducer';
import { useAppDispatch, useAppSelector } from '@common/reducers/hooks/store.hook';
import { selectPlannerCounts } from '@common/reducers/UserMessageSelectors';
import {
    DEFAULT_PLANNER_FILTERS,
    PlannerFilters,
    isNarrowed,
    normalizePlannerFilters,
    plannerFilterConflict,
} from '@common/services/planner-filters';
import { KIND_GLYPH } from '@common/components/kind-glyphs/kind-glyphs';
import { AlertCircle, CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useSentHistoryStatus } from '@common/views/calendar/grid/use-sent-history-status';
import { useDelayedBusy } from '@common/views/calendar/grid/use-delayed-busy';
import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import styles from './calendar-sidebar-filters.module.scss';

type ToggleKey = 'showMessages' | 'showCampaigns' | 'showStatuses' | 'showFailed' | 'showSent';

/**
 * WHAT THIS PANEL IS FOR
 * Three questions, answered without the user having to reason anything out:
 * what am I looking at, what is hiding things from me right now, and how do I get back.
 *
 * THE MODEL (see @common/services/planner-filters — the one place it is interpreted)
 *   - WHAT KIND of send: Scheduled messages · Scheduled campaigns · Statuses.
 *   - WHAT ALREADY HAPPENED: Show failed · Show sent. They apply across every kind that is
 *     on, and they only ever gate RESOLVED sends — anything still to come always shows, which
 *     is what the section's one line of help says, once, instead of on every row.
 *   - WHO it is to: All chats / Groups only. A different axis; it keeps its own small group
 *     now that the "Narrow it down" section (and its Failed / Waiting status chips, which the
 *     outcome toggles made redundant) is gone.
 *
 * Every row is a switch that ADDS what it names, so the panel no longer mixes additive and
 * restrictive controls in one list — the confusion behind the original report.
 *
 * Colour does meaning, not decoration: labels are ink; a kind carries the same glyph it
 * carries on its chip; an outcome carries the planner's own outcome mark (red is failure,
 * and only failure).
 */
export default function CalendarSidebarFilters() {
    const filters = normalizePlannerFilters(useAppSelector(state => state.CalendarReducer.calendarSidebarFilters));
    const counts = useAppSelector(selectPlannerCounts);
    const dispatch = useAppDispatch();
    const { t } = useTranslation(namespace);
    const idBase = useId();
    const kindsId = `${idBase}-kinds`;
    const outcomesId = `${idBase}-outcomes`;
    const chatsId = `${idBase}-chats`;

    const apply = (next: PlannerFilters) => dispatch(setCalendarSidebarFilters(next));
    const toggle = (key: ToggleKey) => apply({ ...filters, [key]: !filters[key] });
    // Only ever offered where the panel has just said the filters are hiding everything
    // ("Clear filters", below). There is no standing Reset or "N active" count in the
    // header: the switches themselves already show every choice, so a counter of them
    // said nothing the panel was not already saying (the product owner's call).
    const reset = () => apply({ ...DEFAULT_PLANNER_FILTERS });

    const narrowed = isNarrowed(filters);
    // "Show sent" makes a request; on a heavy range it takes a while, and a switch that
    // flips with nothing following reads as broken. The same in-flight state the toolbar
    // reads (useSentHistoryStatus), smoothed so a fast answer never blinks.
    const sentHistory = useSentHistoryStatus();
    const sentLoadingVisible = useDelayedBusy(sentHistory.loading);
    // A combination that cannot match anything is NAMED, not left to the user to deduce.
    const conflict = plannerFilterConflict(filters);

    const toggleRow = (
        key: ToggleKey,
        Icon: React.ComponentType<{ className?: string }>,
        title: string,
        tone: 'brand' | 'bad' | 'ok' = 'brand',
        status?: { busy: boolean; loading: boolean; failed: boolean; onRetry: () => void },
    ) => {
        const checked = !!filters[key];
        return (
            <div className={styles.row} onClick={() => toggle(key)} data-filter={key}>
                <span className={styles.mark} data-tone={tone} aria-hidden="true"><Icon /></span>
                <span className={styles.rowText}>
                    <p className={styles.rowTitle}>
                        {title}
                        {/* BESIDE the switch, not in its place: the switch keeps saying what
                            the user chose (and stays clickable — turning it off while loading
                            simply hides sent again; the request finishes into the cache). */}
                        {status?.busy && <Loader2 className={`animate-spin ${styles.rowBusy}`} aria-hidden="true" />}
                    </p>
                    {status && !status.loading && status.failed && (
                        <p className={styles.rowDesc}>
                            {t('calendar.grid.loadSentFailed')}
                            {' '}
                            <button type="button" className={styles.noteAction} onClick={e => { e.stopPropagation(); status.onRetry(); }}>
                                {t('calendar.grid.retry')}
                            </button>
                        </p>
                    )}
                </span>
                <button
                    type="button"
                    role="switch"
                    aria-checked={checked}
                    aria-label={title}
                    aria-busy={status?.loading || undefined}
                    className={styles.switch}
                    onClick={e => { e.stopPropagation(); toggle(key); }}
                >
                    <span className={styles.knob} />
                </button>
            </div>
        );
    };

    return (
        <div className={styles.panel}>
            <div className={styles.head}>
                <h3 className={styles.headTitle}>{t('calendarSidebarFilters.title', { defaultValue: 'Filters' })}</h3>
            </div>

            {/* WHAT KIND of send. */}
            <div className={styles.section} role="group" aria-labelledby={kindsId}>
                <p className={styles.sectionLabel} id={kindsId}>
                    {t('calendarSidebarFilters.sectionShow', { defaultValue: 'What to show' })}
                </p>
                {toggleRow('showMessages', KIND_GLYPH.message,
                    t('calendarSidebarFilters.messages.title', { defaultValue: 'Scheduled messages' }))}
                {toggleRow('showCampaigns', KIND_GLYPH.campaign,
                    t('calendarSidebarFilters.campaigns.title', { defaultValue: 'Scheduled campaigns' }))}
                {toggleRow('showStatuses', KIND_GLYPH.status,
                    t('calendarSidebarFilters.statuses.title', { defaultValue: 'Statuses' }))}
            </div>

            {/* WHAT ALREADY HAPPENED — across every kind that is on. */}
            <div className={styles.section} role="group" aria-labelledby={outcomesId}>
                <p className={styles.sectionLabel} id={outcomesId}>
                    {t('calendarSidebarFilters.sectionOutcome', { defaultValue: 'What already happened' })}
                </p>
                {toggleRow('showFailed', AlertCircle,
                    t('calendarSidebarFilters.showFailed.title', { defaultValue: 'Show failed' }), 'bad')}
                {toggleRow('showSent', CheckCircle2,
                    t('calendarSidebarFilters.showSent.title', { defaultValue: 'Show sent' }), 'ok',
                    { busy: sentLoadingVisible, loading: sentHistory.loading, failed: sentHistory.failed, onRetry: sentHistory.retry })}
                <p className={styles.sectionHint}>
                    {t('calendarSidebarFilters.outcomeHint', { defaultValue: 'Upcoming sends always show.' })}
                </p>
            </div>

            {/* WHO it is to — a different axis, so its own small group. */}
            <div className={styles.section}>
                <p className={styles.sectionLabel} id={chatsId}>
                    {t('calendarSidebarFilters.chatsLabel', { defaultValue: 'Chats' })}
                </p>
                <div className={styles.choice}>
                    <div className={styles.segmented} role="radiogroup" aria-labelledby={chatsId}>
                        <button
                            type="button" role="radio" aria-checked={!filters.groupsOnly}
                            className={styles.segment}
                            onClick={() => apply({ ...filters, groupsOnly: false })}
                        >
                            {t('calendarSidebarFilters.chats.all', { defaultValue: 'All chats' })}
                        </button>
                        <button
                            type="button" role="radio" aria-checked={!!filters.groupsOnly}
                            className={styles.segment}
                            onClick={() => apply({ ...filters, groupsOnly: true })}
                        >
                            {t('calendarSidebarFilters.chats.groups', { defaultValue: 'Groups only' })}
                        </button>
                    </div>
                </div>
            </div>

            {/* WHAT IS HIDING THINGS FROM ME, AND HOW DO I GET BACK. */}
            <div className={styles.foot}>
                {/* "Showing all N" only when it is true. With "Show sent" off — the default —
                    the history that already went out is hidden by definition, and the sent
                    rows are loaded per visible range, so shown === total can hold while sent
                    sends exist that were never counted. "All" would be false there, so the
                    line says what it leaves out instead. The wording names no period — the
                    calendar toolbar counts one month, this counts the whole list.
                    And it names its own UNIT — items in the list, not "sends": a message
                    that repeats weekly is one row here but four or five sends in the
                    toolbar's month, and calling both numbers "sends" ("11 sends" beside
                    "12 sends this month") made the right one look wrong. */}
                <p className={styles.count}>
                    {counts.shown === counts.total
                        ? (filters.showSent
                            ? t('calendarSidebarFilters.showingAll', {
                                count: counts.shown, defaultValue: 'Showing all {{count}} items in the list',
                            })
                            : t('calendarSidebarFilters.showingUnsent', {
                                count: counts.shown, defaultValue: '{{count}} items in the list, not counting sent ones',
                            }))
                        : t('calendarSidebarFilters.showingCount', {
                            shown: counts.shown, total: counts.total,
                            defaultValue: '{{shown}} of {{total}} items in the list match the filters',
                        })}
                </p>

                {(conflict || counts.shown === 0) && (
                    <p className={styles.note}>
                        <span className={styles.noteIcon} aria-hidden="true"><Info /></span>
                        <span>
                            {conflict === 'nothing-selected'
                                ? t('calendarSidebarFilters.conflictNoKinds', {
                                    defaultValue: 'Every kind of send is switched off, so there is nothing left to show.',
                                })
                                : narrowed
                                    ? t('calendarSidebarFilters.emptyByFilters', {
                                        defaultValue: 'Your filters are hiding everything — this is not an empty schedule.',
                                    })
                                    : counts.total > 0
                                        // The default filters, and everything there is has already
                                        // gone out: Reset would change nothing (sent is OFF by
                                        // default), so the honest pointer is the Show sent switch.
                                        ? t('calendarSidebarFilters.emptyAllSent', {
                                            defaultValue: 'Nothing is scheduled. Everything here already went out — turn on Show sent to see it.',
                                        })
                                        : t('calendarSidebarFilters.emptyForReal', {
                                            defaultValue: 'You have nothing scheduled yet.',
                                        })}
                            {(conflict || narrowed) && <br />}
                            {(conflict || narrowed) && (
                                <button type="button" className={styles.noteAction} onClick={reset}>
                                    {t('calendarSidebarFilters.clearFilters', { defaultValue: 'Clear filters' })}
                                </button>
                            )}
                        </span>
                    </p>
                )}
            </div>
        </div>
    );
}
